/**
 * RuleExceptions API Module
 *
 * This module provides functions for interacting with rule exception-related endpoints
 * of the WealthBee Enterprise API.
 */
import axios from "axios";
import { RuleException } from "../types/RuleException";
import getServiceEndpoint from "./ServiceEndpoint";
import { buildClientHeader } from "./buildHeader";

/**
 * Retrieves rule exceptions for a specific reporting period.
 *
 * @param reportingPeriodId - The ID of the reporting period to get rule exceptions for
 * @returns A promise that resolves to an array of RuleException objects.
 * If the request fails, returns an empty array.
 */
export const getRuleExceptions = async (reportingPeriodId: number): Promise<RuleException[]> => {
  try {
    const response = await axios.get(
      `${getServiceEndpoint()}/RuleExceptions`,
      {
        ...buildClientHeader(),
        params: {
          reportingPeriodId,
        },
      }
    );

    if (response.status !== 200 || response.data === undefined) {
      console.log("Failed to retrieve rule exceptions", response.statusText);
      return [];
    }

    return response.data ?? [];
  } catch (error) {
    console.error("Error getting rule exceptions:", error);
    return [];
  }
};

/**
 * Creates a new rule exception.
 *
 * @param ruleException - The rule exception data to create
 * @returns A promise that resolves to a boolean indicating success (true) or failure (false).
 */
export const createRuleException = async (ruleException: RuleException): Promise<boolean> => {
  try {
    const response = await axios.post(
      `${getServiceEndpoint()}/RuleExceptions`,
      ruleException,
      buildClientHeader()
    );

    if (response.status !== 200 || response.data === undefined) {
      console.error("Failed to create rule exception", response.statusText);
      return false;
    }

    return response.data === true;
  } catch (error) {
    console.error("Error creating rule exception:", error);
    return false;
  }
}; 